import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiBarChart2,
  FiCalendar,
  FiDownload,
  FiRefreshCw,
  FiPlus,
  FiSearch,
  FiX,
} from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";
import usePurchasingReadAccess from "../components/usePurchasingReadAccess";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import useCrossTabDataRefreshToken from "../../hooks/useCrossTabDataRefresh";
import useManagementDialog from "../hooks/useManagementDialog";
import { reportsText } from "./reportsMessages";
import "../PurchasingUi.css";
import "./Reports.css";

const REPORT_VIEW = "purchasing.reports.items_purchased.view";
const REPORT_EXPORT = [REPORT_VIEW, "purchasing.reports.items_purchased.export"];

const GROUP_FIELDS = [
  { key: "equivalent", label: "Equivalent product" },
  { key: "supplier", label: "Supplier" },
  { key: "family", label: "Family" },
  { key: "subfamily", label: "Subfamily" },
  { key: "category", label: "Category" },
  { key: "subcategory", label: "Subcategory" },
  { key: "item", label: "Item" },
  { key: "deliveryNote", label: "Delivery note" },
  { key: "deliveryDate", label: "Delivery date" },
];

const GROUP_FIELD_MAP = GROUP_FIELDS.reduce((acc, field) => {
  acc[field.key] = field;
  return acc;
}, {});

function dateOnly(daysOffset = 0) {
  const date = new Date();
  date.setDate(date.getDate() + daysOffset);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function formatDate(value) {
  if (!value) return "-";
  const dateOnlyMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    return `${dateOnlyMatch[3]}/${dateOnlyMatch[2]}/${dateOnlyMatch[1]}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatDateRange(start, end) {
  if (!start && !end) return "-";
  if (start === end) return formatDate(start);
  return `${formatDate(start)} - ${formatDate(end)}`;
}

function labelWithCode(label, code) {
  const cleanLabel = label || "-";
  return code ? `${cleanLabel} (${code})` : cleanLabel;
}

function getGroupValue(row, key) {
  switch (key) {
    case "equivalent":
      return labelWithCode(row.equivalentName, row.equivalentCode);
    case "supplier":
      return labelWithCode(row.supplierName, row.supplierCode);
    case "family":
      return row.familyName || "-";
    case "subfamily":
      return row.subfamilyName || "-";
    case "category":
      return row.categoryName || "-";
    case "subcategory":
      return row.subcategoryName || "-";
    case "item":
      return labelWithCode(row.itemName, row.itemCode);
    case "deliveryNote":
      return `${row.deliveryNoteNumber || "-"} · ${formatDate(row.deliveryNoteDate)}`;
    case "deliveryDate":
      return formatDate(row.deliveryDate || row.deliveryNoteDate);
    default:
      return "-";
  }
}

function moveItem(items, index, direction) {
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= items.length) return items;
  const next = [...items];
  const [item] = next.splice(index, 1);
  next.splice(nextIndex, 0, item);
  return next;
}

function GroupingPicker({ options, counts, onSelect, onClose, themeId, t }) {
  const [search, setSearch] = useState("");
  const dialogRef = useManagementDialog(onClose, false);
  const visible = options.filter((field) => t(field.label).toLocaleLowerCase().includes(search.toLocaleLowerCase().trim()));
  return createPortal(
    <div className="pilcore-purchasing-overlay ipr-groupOverlay" data-theme={themeId} onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="ipr-groupDialog" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="ipr-group-title" tabIndex={-1}>
        <header><div><h2 id="ipr-group-title">{t("Add grouping")}</h2><p>{t("Choose a field to summarize purchases. Add more to create a hierarchy.")}</p></div><button className="ipr-iconButton" type="button" onClick={onClose} aria-label={t("Close")}><FiX /></button></header>
        <label className="ipr-search"><FiSearch aria-hidden="true" /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t("Search fields…")} aria-label={t("Search fields…")} /></label>
        <div className="ipr-groupOptions">
          {visible.map((field) => <button type="button" key={field.key} onClick={() => { onSelect(field.key); onClose(); }}><span>{t(field.label)}</span><small>{counts.has(field.key) ? `${counts.get(field.key)} ${t("distinct values")}` : t("Group by this field")}</small><FiPlus aria-hidden="true" /></button>)}
          {!visible.length && <p className="ipr-empty">{t("No matching fields.")}</p>}
        </div>
      </section>
    </div>, document.body
  );
}

function Reports() {
  const { themeId, languageId } = usePmsTheme();
  const t = (text) => reportsText(text, languageId);
  const locale = languageId === "es" ? "es-ES" : "en-GB";
  const formatters = useMemo(() => ({
    number: new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }),
    money: new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }),
    unit: new Intl.NumberFormat(locale, { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 4 }),
  }), [locale]);
  const formatNumber = (value) => formatters.number.format(Number(value || 0));
  const formatMoney = (value) => formatters.money.format(Number(value || 0));
  const formatUnitMoney = (value) => formatters.unit.format(Number(value || 0));
  const sessionUser = readStoredUser();
  const [searchParams] = useSearchParams();
  const businessUnitId = String(searchParams.get("business_unit_id") || sessionUser?.active_business_unit_id || "");
  const access = usePurchasingReadAccess(businessUnitId);
  const canView = access.can(REPORT_VIEW);
  const canExport = access.can(REPORT_EXPORT);

  const [startDate, setStartDate] = useState(() => dateOnly(-29));
  const [endDate, setEndDate] = useState(() => dateOnly(0));
  const [groupBy, setGroupBy] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshToken, setRefreshToken] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [showGrouping, setShowGrouping] = useState(false);
  const [loadedQuery, setLoadedQuery] = useState("");
  const crossTabRefreshToken = useCrossTabDataRefreshToken();

  const groupByParam = groupBy.join(",");
  const queryKey = JSON.stringify([access.contextKey, startDate, endDate, groupByParam]);
  const currentQuery = useRef({ key: queryKey, generation: 0 });
  if (currentQuery.current.key !== queryKey) {
    currentQuery.current = { key: queryKey, generation: currentQuery.current.generation + 1 };
  }
  const validDates = Boolean(startDate && endDate && startDate <= endDate);

  useEffect(() => {
    setExportError("");
    if (!canView || (sessionUser?.active_business_unit_id && String(businessUnitId) !== String(sessionUser.active_business_unit_id))) {
      setReport(null); setError(!canView ? "You do not have access to this report." : "Select a business unit to view this report."); setLoading(false); return undefined;
    }
    if (!businessUnitId || !validDates) {
      setError("Date range is not valid.");
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    setError("");
    const timeoutId = window.setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          group_by: groupByParam,
          limit: "750",
        });

        const response = await axios.get(`/api/purchasing/reports/items-purchased?${params.toString()}`, {
          signal: controller.signal,
          ...access.requestConfig(REPORT_VIEW),
        });
        if (!controller.signal.aborted && access.can(REPORT_VIEW, true)) {
          setReport(response.data);
          setLoadedQuery(queryKey);
        }
      } catch (err) {
        if (controller.signal.aborted || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        setError(err?.response?.data?.error || "Items purchased report could not be loaded.");
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [businessUnitId, startDate, endDate, groupByParam, queryKey, validDates, refreshToken, crossTabRefreshToken, canView, sessionUser?.id, sessionUser?.active_business_unit_id, access]);

  const availableCounts = useMemo(() => {
    return new Map((report?.availableGroupFields || []).map((field) => [field.key, field.distinctCount]));
  }, [report]);

  const groupOptions = useMemo(() => {
    return GROUP_FIELDS.filter((field) => {
      if (groupBy.includes(field.key)) return false;
      const equivalentFields = ["equivalent", "supplier", "item"];
      if (groupBy.includes("equivalent") && !equivalentFields.includes(field.key)) return false;
      if (field.key === "equivalent" && groupBy.some((key) => !equivalentFields.includes(key))) return false;
      if (!availableCounts.size) return true;
      return Number(availableCounts.get(field.key) || 0) > 0;
    });
  }, [availableCounts, groupBy]);

  const isGrouped = groupBy.length > 0;
  const isEquivalent = groupBy.includes("equivalent");
  const equivalentUnit = (row) => row.unit === "unit" ? (languageId === "es" ? "ud" : "unit") : row.unit || "—";
  const currencyMoney = (value, row) => value == null ? "—" : new Intl.NumberFormat(locale, { style: "currency", currency: /^[A-Z]{3}$/.test(row.currency || "") ? row.currency : "EUR", maximumFractionDigits: 4 }).format(Number(value));
  const activeGroupColumns = groupBy.map((key) => GROUP_FIELD_MAP[key]).filter(Boolean);
  const reportMatchesFilters =
    loadedQuery === queryKey &&
    report?.window?.startDate === startDate &&
    report?.window?.endDate === endDate &&
    (Array.isArray(report?.groupBy) ? report.groupBy.join(",") : "") === groupByParam;

  const summary = reportMatchesFilters ? report?.summary || {} : {};
  const rows = reportMatchesFilters && !error && Array.isArray(report?.rows) ? report.rows : [];

  const addGrouping = (key) => {
    if (!key) return;
    setGroupBy((prev) => (prev.includes(key) ? prev : [...prev, key]));
  };

  const removeGrouping = (key) => {
    setGroupBy((prev) => prev.filter((item) => item !== key));
  };

  const resetGrouping = () => {
    setGroupBy([]);
  };

  const exportReport = async () => {
    if (!access.can(REPORT_EXPORT, true) || !reportMatchesFilters || loading || exporting || !rows.length) return;

    const exportGeneration = currentQuery.current.generation;
    setExporting(true);
    setExportError("");
    try {
      const ensureExportAccess = () => {
        if (!access.can(REPORT_EXPORT, true) || currentQuery.current.generation !== exportGeneration) {
          throw new Error("Your session, access or report filters changed during export.");
        }
      };
      // Re-fetch even a single page so the server checks the persisted export
      // grant and current item/location scope before any file is generated.
      const pageSize = 2000;
      const exportRows = [];
      let expectedRows = Infinity;
      let offset = 0;
      let exportSummary = {};
      while (offset < expectedRows) {
        ensureExportAccess();
        const params = new URLSearchParams({
          start_date: startDate, end_date: endDate,
          group_by: groupByParam, limit: String(pageSize), offset: String(offset), export: "1",
        });
        const response = await axios.get(`/api/purchasing/reports/items-purchased?${params.toString()}`, access.requestConfig(REPORT_EXPORT));
        ensureExportAccess();
        const pageRows = Array.isArray(response.data?.rows) ? response.data.rows : [];
        if (offset === 0) {
          exportSummary = response.data?.summary || {};
          expectedRows = Number(exportSummary.rowCount ?? pageRows.length);
        }
        exportRows.push(...pageRows);
        if (!pageRows.length || pageRows.length < pageSize) break;
        offset += pageRows.length;
      }
      ensureExportAccess();
      const dataRows = isGrouped
        ? exportRows.map((row) => {
            const exportedRow = {};
            activeGroupColumns.forEach((field) => {
              exportedRow[t(field.label)] = getGroupValue(row, field.key);
            });
            exportedRow[t(isEquivalent ? "Normalized quantity" : "Qty purchased")] = row.quantity == null ? null : Number(row.quantity);
            if (isEquivalent) { exportedRow[t("Common unit")] = equivalentUnit(row); exportedRow[t("Currency")] = row.currency || ""; }
            exportedRow[t("Avg unit price")] = row.averageUnitPrice == null ? null : Number(row.averageUnitPrice);
            exportedRow[t("Total purchase")] = row.totalValue == null ? null : Number(row.totalValue);
            exportedRow[t("Lines")] = row.purchaseLineCount == null ? null : Number(row.purchaseLineCount);
            exportedRow[t("Delivery notes")] = row.deliveryNoteCount == null ? null : Number(row.deliveryNoteCount);
            exportedRow[t("First delivery date")] = formatDate(row.firstDeliveryDate);
            exportedRow[t("Last delivery date")] = formatDate(row.lastDeliveryDate);
            return exportedRow;
          })
        : exportRows.map((row) => ({
            [t("Delivery date")]: formatDate(row.deliveryNoteDate),
            [t("Delivery note")]: row.deliveryNoteNumber || "-",
            [t("Status")]: t(row.deliveryNoteStatus || "-"),
            [t("Supplier code")]: row.supplierCode || "",
            [t("Supplier")]: row.supplierName || "-",
            [t("Family")]: row.familyName || "-",
            [t("Subfamily")]: row.subfamilyName || "-",
            [t("Category")]: row.categoryName || "-",
            [t("Subcategory")]: row.subcategoryName || "-",
            [t("Item code")]: row.itemCode || "",
            [t("Item")]: row.itemName || "-",
            [t("Qty purchased")]: Number(row.quantity || 0),
            [t("Unit price")]: Number(row.averageUnitPrice || 0),
            [t("Total purchase")]: Number(row.totalValue || 0),
            [t("Currency")]: row.currency || "EUR",
          }));

      const workbook = XLSX.utils.book_new();
      const dataSheet = XLSX.utils.json_to_sheet(dataRows);
      if (dataRows.length) {
        dataSheet["!autofilter"] = {
          ref: `A1:${XLSX.utils.encode_col(Object.keys(dataRows[0]).length - 1)}${dataRows.length + 1}`,
        };
      }
      dataSheet["!cols"] = Object.keys(dataRows[0] || {}).map((column) => ({
        wch: Math.min(Math.max(column.length + 2, 14), 34),
      }));
      XLSX.utils.book_append_sheet(workbook, dataSheet, t("Items purchased"));

      const summarySheet = XLSX.utils.json_to_sheet([
        {
          [t("Report")]: t("Items purchased"),
          [t("From")]: formatDate(startDate),
          [t("To")]: formatDate(endDate),
          [t("Grouping")]: activeGroupColumns.map((field) => t(field.label)).join(" > ") || t("Detailed lines"),
          [t("Total purchase")]: exportSummary.totalValue == null ? null : Number(exportSummary.totalValue),
          [t(isEquivalent ? "Normalized quantity" : "Qty purchased")]: exportSummary.quantity == null ? null : Number(exportSummary.quantity),
          [t("Avg unit price")]: exportSummary.averageUnitPrice == null ? null : Number(exportSummary.averageUnitPrice),
          [t("Delivery notes")]: exportSummary.deliveryNoteCount == null ? null : Number(exportSummary.deliveryNoteCount),
          [t("Lines")]: exportSummary.purchaseLineCount == null ? null : Number(exportSummary.purchaseLineCount),
          [t("Rows exported")]: dataRows.length,
          [t("Generated")]: new Date().toLocaleString(locale),
        },
      ]);
      summarySheet["!cols"] = [
        { wch: 20 },
        { wch: 14 },
        { wch: 14 },
        { wch: 28 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 14 },
        { wch: 16 },
        { wch: 22 },
      ];
      XLSX.utils.book_append_sheet(workbook, summarySheet, t("Summary"));

      const groupingSuffix = groupBy.length ? groupBy.join("-") : "detail";
      ensureExportAccess();
      XLSX.writeFile(
        workbook,
        `items_purchased_${startDate}_${endDate}_${groupingSuffix}.xlsx`,
        { compression: true }
      );
    } catch (requestError) {
      console.error("[Purchasing][Reports][ItemsPurchased][Export]", requestError);
      setExportError(requestError?.response?.data?.error || "The Excel report could not be exported.");
    } finally {
      setExporting(false);
    }
  };

  const renderGroupedTable = () => (
    <table className="ipr-table" aria-label={t("Items purchased")}>
      <thead>
        <tr>
          {activeGroupColumns.map((field) => (
            <th key={field.key} scope="col">{t(field.label)}</th>
          ))}
          <th className="ipr-num" scope="col">{t(isEquivalent ? "Normalized quantity" : "Qty purchased")}</th>
          {isEquivalent && <><th scope="col">{t("Common unit")}</th><th scope="col">{t("Currency")}</th></>}
          <th className="ipr-num" scope="col">{t("Avg unit price")}</th>
          <th className="ipr-num" scope="col">{t("Total purchase")}</th>
          <th className="ipr-num" scope="col">{t("Lines")}</th>
          <th className="ipr-num" scope="col">{t("DNs")}</th>
          <th scope="col">{t("Date range")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={`group-${rowIndex}-${activeGroupColumns.map((field) => getGroupValue(row, field.key)).join("-")}`}>
            {activeGroupColumns.map((field) => (
              <td key={`${rowIndex}-${field.key}`}>
                <div className="ipr-mainCell">{getGroupValue(row, field.key)}</div>
              </td>
            ))}
            <td className="ipr-num">{row.quantity == null ? "—" : formatNumber(row.quantity)}</td>
            {isEquivalent && <><td>{equivalentUnit(row)}</td><td>{row.currency}</td></>}
            <td className="ipr-num">{isEquivalent ? currencyMoney(row.averageUnitPrice, row) : formatUnitMoney(row.averageUnitPrice)}</td>
            <td className="ipr-num">{isEquivalent ? currencyMoney(row.totalValue, row) : formatMoney(row.totalValue)}</td>
            <td className="ipr-num">{row.purchaseLineCount == null ? "—" : formatNumber(row.purchaseLineCount)}</td>
            <td className="ipr-num">{row.deliveryNoteCount == null ? "—" : formatNumber(row.deliveryNoteCount)}</td>
            <td>{formatDateRange(row.firstDeliveryDate, row.lastDeliveryDate)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderDetailTable = () => (
    <table className="ipr-table" aria-label={t("Items purchased")}>
      <thead>
        <tr>
          <th scope="col">{t("Date")}</th>
          <th scope="col">{t("Delivery note")}</th>
          <th scope="col">{t("Supplier")}</th>
          <th scope="col">{t("Family")}</th>
          <th scope="col">{t("Subfamily")}</th>
          <th scope="col">{t("Category")}</th>
          <th scope="col">{t("Subcategory")}</th>
          <th scope="col">{t("Item")}</th>
          <th className="ipr-num" scope="col">{t("Qty purchased")}</th>
          <th className="ipr-num" scope="col">{t("Unit price")}</th>
          <th className="ipr-num" scope="col">{t("Total purchase")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.deliveryNoteLineId}>
            <td>{formatDate(row.deliveryNoteDate)}</td>
            <td>
              <div className="ipr-mainCell">{row.deliveryNoteNumber || "-"}</div>
              <div className="ipr-mutedCell">{t(row.deliveryNoteStatus || "")}</div>
            </td>
            <td>{labelWithCode(row.supplierName, row.supplierCode)}</td>
            <td>{row.familyName || "-"}</td>
            <td>{row.subfamilyName || "-"}</td>
            <td>{row.categoryName || "-"}</td>
            <td>{row.subcategoryName || "-"}</td>
            <td>
              <div className="ipr-mainCell">{row.itemName || "-"}</div>
              {row.itemCode ? <div className="ipr-mutedCell">{row.itemCode}</div> : null}
            </td>
            <td className="ipr-num">{formatNumber(row.quantity)}</td>
            <td className="ipr-num">{formatUnitMoney(row.averageUnitPrice)}</td>
            <td className="ipr-num">{formatMoney(row.totalValue)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  if (!canView) return <div className="pilcore-purchasing-ui ipr-page" data-theme={themeId}><p className="ipr-empty" role="alert">{t("You do not have access to this report.")}</p></div>;
  return (
    <div className="pilcore-purchasing-ui ipr-page" data-theme={themeId}>
      <div className="ipr-shell">
        <header className="ipr-header">
          <div className="ipr-titleWrap">
            <span className="ipr-eyebrow"><FiBarChart2 aria-hidden="true" /> {t("Reports")}</span>
            <h1 className="ipr-title">{t("Items purchased")}</h1>
            <p className="ipr-subtitle">{t("Review purchases by period, supplier or item.")}</p>
          </div>
          {canExport && <button type="button" className="ipr-button ipr-exportButton" onClick={exportReport}
            disabled={loading || exporting || !rows.length || !reportMatchesFilters}
            title={t("Export the current filtered report to Excel")}>
            <FiDownload aria-hidden="true" /> {t(exporting ? "Exporting..." : "Export Excel")}
          </button>}
        </header>

        <section className="ipr-toolbar" aria-label={t("Items purchased filters")}>
          <div className="ipr-dateFields">
            <FiCalendar aria-hidden="true" />
            <label className="ipr-field"><span>{t("From")}</span>
              <input className="ipr-input" type="date" value={startDate} aria-invalid={!validDates}
                onChange={(event) => setStartDate(event.target.value)} />
            </label>
            <label className="ipr-field"><span>{t("To")}</span>
              <input className="ipr-input" type="date" value={endDate} aria-invalid={!validDates}
                onChange={(event) => setEndDate(event.target.value)} />
            </label>
          </div>
          <button type="button" className="ipr-button" onClick={() => setShowGrouping(true)}
            aria-haspopup="dialog" disabled={!groupOptions.length}>
            <FiPlus aria-hidden="true" /> {t("Add grouping")}
          </button>
          <button className="ipr-button" type="button" onClick={() => setGroupBy(["equivalent", "supplier", "item"])}>{t("Equivalent → Supplier → Item")}</button>
          {!groupBy.length && <span className="ipr-detailHint">{t("Detailed lines")}</span>}
        </section>

        {groupBy.length > 0 && <div className="ipr-groups" aria-label={t("Grouping")}>
          <span className="ipr-groupsLabel">{t("Grouping")}</span>
          {groupBy.map((key, index) => (
            <span key={key} className="ipr-chip">
              <span className="ipr-chipOrder">{index + 1}</span>
              {t(GROUP_FIELD_MAP[key]?.label || key)}
              <button type="button" className="ipr-iconButton" title={t("Move left")}
                aria-label={t("Move left") + ": " + t(GROUP_FIELD_MAP[key]?.label)}
                disabled={index === 0} onClick={() => setGroupBy((prev) => moveItem(prev, index, -1))}><FiArrowLeft /></button>
              <button type="button" className="ipr-iconButton" title={t("Move right")}
                aria-label={t("Move right") + ": " + t(GROUP_FIELD_MAP[key]?.label)}
                disabled={index === groupBy.length - 1} onClick={() => setGroupBy((prev) => moveItem(prev, index, 1))}><FiArrowRight /></button>
              <button type="button" className="ipr-iconButton" title={t("Remove")}
                aria-label={t("Remove") + ": " + t(GROUP_FIELD_MAP[key]?.label)} onClick={() => removeGrouping(key)}><FiX /></button>
            </span>
          ))}
          <button type="button" className="ipr-clearButton" onClick={resetGrouping}>{t("Clear groups")}</button>
        </div>}

        {error && <div className="ipr-error" role="alert"><span>{t(error)}</span>
          {validDates && <button type="button" className="ipr-button" onClick={() => setRefreshToken((value) => value + 1)}><FiRefreshCw aria-hidden="true" />{t("Retry")}</button>}
        </div>}
        {exportError && <div className="ipr-error" role="alert">{t(exportError)}</div>}

        {isEquivalent && <div className="ipr-status"><p>{t("Quantities and prices are comparable within each equivalent product and currency only.")} {reportMatchesFilters && Number(report?.coverage?.excludedLineCount || 0) > 0 && <strong>{t("Excluded lines")}: {report.coverage.excludedLineCount}. {t("Only purchases with a valid conversion and known price are included.")}</strong>}</p></div>}
        <section className="ipr-stats" aria-label={t("Items purchased summary")}>
          {[
            ["Total purchase", summary.totalValue == null && isEquivalent ? "—" : formatMoney(summary.totalValue)],
            [isEquivalent ? "Normalized quantity" : "Qty purchased", summary.quantity == null && isEquivalent ? "—" : formatNumber(summary.quantity)],
            ["Avg unit price", summary.averageUnitPrice == null && isEquivalent ? "—" : formatUnitMoney(summary.averageUnitPrice)],
            ["Delivery notes", summary.deliveryNoteCount == null ? "—" : formatNumber(summary.deliveryNoteCount)],
            ["Lines", summary.purchaseLineCount == null ? "—" : formatNumber(summary.purchaseLineCount)],
          ].map(([label, value]) => <div className="ipr-stat" key={label}><span>{t(label)}</span><strong>{reportMatchesFilters && !error ? value : "—"}</strong></div>)}
        </section>

        <section className="ipr-tableWrap" aria-label={t(isGrouped ? "Grouped purchases" : "Purchase details")}>
          <div className="ipr-status">
            <strong>{t(isGrouped ? "Grouped purchases" : "Purchase details")}</strong>
            <span role="status">{loading ? t("Loading...") : !reportMatchesFilters || error ? "—" : summary.isTruncated
              ? t("Showing") + " " + formatNumber(rows.length) + " " + t("of") + " " + formatNumber(summary.rowCount) + " · " + t("Excel includes all rows.")
              : formatNumber(summary.rowCount || rows.length) + " " + t("rows")}</span>
          </div>
          <div className="ipr-tableScroller" tabIndex={0} aria-label={t("Items purchased")} aria-busy={loading}>
            {rows.length ? (isGrouped ? renderGroupedTable() : renderDetailTable()) : (
              <div className="ipr-empty">
                <FiBarChart2 size={28} aria-hidden="true" />
                <strong>{t(loading ? "Loading items purchased..." : error ? error : "No purchases found for this selection.")}</strong>
                {!loading && !error && <span>{t("Try another period or remove the groupings.")}</span>}
              </div>
            )}
          </div>
        </section>
      </div>
      {showGrouping && <GroupingPicker options={groupOptions} counts={availableCounts} onSelect={addGrouping}
        onClose={() => setShowGrouping(false)} themeId={themeId} t={t} />}
    </div>
  );
}

export default Reports;
